import React, { useEffect, useMemo } from "react";
import OnSubmitValidationError from "../../../components/FormComponents/OnSubmitValidationError";
import { connect } from "react-redux";
import * as interviewActions from "../../../actions/interviewActions";
import { Field, FieldArray, Formik } from "formik";
import * as formsService from "../../../services/formsService";
import { validationSchema } from "../InterviewPatientInfoScreening/validationSchema";
import { WizardButtonsField } from "../Components/WizardButtons";
import { CheckboxGroupWithValue } from "../../../components/FormComponents/CheckboxGroupWithValue";
import { YesNoButtons } from "../../../components/FormComponents/YesNoButtons";

function getStudents(householdMembers, responsibleParty, patientId) {
    let potentialStudents = [...householdMembers];
    if (responsibleParty?.personId != null) {
        potentialStudents = [responsibleParty, ...householdMembers];
    }

    return potentialStudents
        .filter(x => x.personId !== patientId)
        .filter(x => x.age >= 19 && x.age <= 26)
        .map(x => {
            if (x.personId === responsibleParty.personId) {
                return {
                    label: `${responsibleParty.fullName} (${x.relationshipToPatient})`,
                    value: x.personId,
                };
            } else {
                return {
                    label: `${x.fullName} (${x.relationshipToPatient})`,
                    value: x.personId,
                };
            }
        });
}

const UmcFaForm = ({
    onError,
    patientFullName,
    potentialStudents,
    navigateToNextPage,
    navigateToPreviousPage,
    ...props
}) => {
    const { setFieldValue, setFieldTouched, resetForm, handleSubmit, errors, touched, values } = props;
    const getValidationState = formsService.getValidationState.bind(this, errors, touched);
    const getErrors = formsService.getErrors.bind(this, errors);
    const isRequired = formsService.isRequired.bind(this, validationSchema, values);
    const potentialDependents = useMemo(() => {
        return values.students;
    }, [values]);

    return (
        <form className="wizard-form">
            <OnSubmitValidationError callback={onError} />
            <div className="form-section">
                <div className="form-control">
                    <Field
                        name="students"
                        render={({ field }) => (
                            <CheckboxGroupWithValue
                                title={`Please select any student in ${patientFullName}'s household.`}
                                changeEvent={(e, i) => {
                                    let valueToSet = [...i];
                                    setFieldValue(field.name, valueToSet);
                                }}
                                groupName={"interview-fa-umc-studentList"}
                                items={potentialStudents}
                                selectedValues={field.value}
                            />
                        )}
                    />
                </div>
                {potentialDependents.length > 0 &&
                    potentialDependents.map(x => {
                        return (
                            <div className="form-section" key={x.value}>
                                <div className="form-control">
                                    <YesNoButtons
                                        name={`dependents.${x.value}`}
                                        title={`Does ${x.label} depend on ${patientFullName} for 50% of their income?`}
                                        setFieldValue={setFieldValue}
                                        getErrors={getErrors}
                                        getValidationState={getValidationState}
                                        isRequired={isRequired}
                                    />
                                </div>
                            </div>
                        );
                    })}
            </div>
            <WizardButtonsField
                shouldAutoNavigate={false}
                navigateToNextPage={navigateToNextPage}
                // navigateToNextPage={handleSubmit}
                navigateToPreviousPage={navigateToPreviousPage}
            />
        </form>
    );
};

const UmcFinancialAssistance = ({
    onError,
    patientId,
    patientFullName,
    navigateToNextPage,
    navigateToPreviousPage,
    financialAssistance,
    householdMembers,
    responsibleParty,
    onSubmit,
    currentSection,
}) => {
    let initialValues = financialAssistance;
    const potentialStudents = useMemo(() => getStudents(householdMembers, responsibleParty, patientId), [
        householdMembers,
        responsibleParty,
        patientId,
    ]);

    if (financialAssistance?.students) {
        const studentIds = financialAssistance?.students;

        const students = potentialStudents.filter(x => studentIds.indexOf(x.value) >= 0);

        initialValues = {
            students: students,
            dependents: financialAssistance.dependents,
        };
    } else {
        initialValues = {
            students: [],
            dependents: {},
        };
    }

    const onSubmitInternal = formData => {
        let students = [];
        if (formData.students) {
            students = formData.students.map(x => x.value);
        }
        const dataToSubmit = {
            students: students,
            dependents: formData.dependents || {},
        };

        onSubmit(dataToSubmit);
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={onSubmitInternal}
            render={props => {
                return (
                    <UmcFaForm
                        navigateToNextPage={navigateToNextPage}
                        navigateToPreviousPage={navigateToPreviousPage}
                        onError={onError}
                        patientFullName={patientFullName}
                        potentialStudents={potentialStudents}
                        {...props}
                    />
                );
            }}
        />
    );
};

export const InterviewUmcFinancialAssistance = connect(
    (state, ownProps) => ({
        patientId: state.interview.entry.patient.personId,
        patientFullName: state.interview.entry.patient.fullName,
        patientAge: state.interview.entry.patient.age,
        financialAssistance: state.interview.financialAssistance,
        householdMembers: state.interview.householdMembers,
        responsibleParty: state.interview.entry.responsibleParty,
        currentSection: ownProps.currentSection,
    }),
    dispatch => ({
        onSubmit: data => dispatch(interviewActions.setPatientFinancialAssistanceInfo(data)),
        onError: () => dispatch(interviewActions.setInterviewError("info")),
    })
)(UmcFinancialAssistance);
